import {
  TreeNodeAddModel,
  TreeNodeDragModel,
  TreeNodeEditModel,
  TreeNodeRemoveModel,
} from '@/components/nodesTree/interfaces';
import {SystemNodeType} from '@/components/system/enums';
import {
  ErrorPeriod,
  PeriodsData,
  ProductionData,
  StatusPeriod,
  SystemNode,
  SystemNodeData,
} from '@/components/system/interfaces';
import {httpClient} from '@api/baseApi';
import {NodeConfig, NodeLink} from './configApi';

/*Информация о сигнале*/
export interface ConfigTypeValueInfo {
  alias: string;
  name: string;
  phys: string;
  shortName: string;
  unit: string;
  yAxisDesc: string;
}

/*Информация о спектре*/
export interface ConfigTypeSpectrumValueInfo {
  eventType: string;
  spectrumName: string;
  unit: string;
  yAxisDesc: string;
}

export async function requestUpdatingPModules(nodeId: string, pModules: NodeConfig[]) {
  const transformPModulesData = pModules.map((elem: NodeConfig) => ({
    config_id: elem.id,
    coords: elem.coords,
  }));
  const {data} = await httpClient.put(`nodes/${nodeId}/configs/update`, {
    configs: transformPModulesData,
  });
  const transformResultData: NodeConfig[] = data.map((elem: any) => ({
    id: elem.config_id,
    coords: elem.coords,
  }));
  return transformResultData;
}

export async function requestUpdatingLinks(nodeId: string, links: NodeLink[]) {
  const transformLinkData = links.map((elem: NodeLink) => ({
    node_id: elem.id,
    coords: elem.coords,
  }));
  const {data} = await httpClient.put(`nodes/${nodeId}/nodes/update`, {
    children: transformLinkData,
  });
  const transformResultData: NodeLink[] = data.map((elem: any) => ({
    id: elem.node_id,
    coords: elem.coords,
  }));
  return transformResultData;
}

export async function requestUploadingScheme(nodeId: string, formData: FormData) {
  const {data} = await httpClient.post<{content: string}>(
    `nodes/${nodeId}/schema/upload/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return data.content;
}

export async function requestUpdatingConfigs(nodeId: string, modules: string[]) {
  await httpClient.put(`nodes/${nodeId}/configs/set`, {ids: modules});
}

export async function requestScheme(nodeId: string) {
  try {
    const {data} = await httpClient.get<string>(`nodes/${nodeId}/schema/`);
    return data;
  } catch (e) {
    console.log('Error on Scheme request');
    return null;
  }
}

// Получить дерево узлов
export async function requestNodeTree(): Promise<SystemNode[]> {
  try {
    const {data} = await httpClient.get(`nodes/`);
    return data;
  } catch (e) {
    console.log('Error on Node tree request');
    return [];
  }
}

// Получить модель на создание узла
export async function getTreeNodeAddModel() {
  const {data} = await httpClient.get(`nodes/models/create`);
  const transformedData: TreeNodeAddModel = {
    name: data.name,
    type: data.type,
    parentNodeId: data.parent_node_id,
  };
  return transformedData;
}

// Получить модель на редактирование узла
export async function getTreeNodeEditModel(id: string) {
  const {data} = await httpClient.get(`nodes/models/${id}`);
  const transformedData: TreeNodeEditModel = {
    id: data.id,
    name: data.name,
    type: data.type,
    parentNodeId: data.parent_node_id,
  };
  return transformedData;
}

// Добавить новый узел в дерево
export async function addTreeNode(model: TreeNodeAddModel) {
  const {data} = await httpClient.post(`nodes/`, {
    name: model.name,
    type: model.type,
    parent_node_id: model.parentNodeId,
  });
  // Возвращает созданный узел с присвоенным идентификатором
  const transformedData: TreeNodeEditModel = {
    id: data.id,
    name: data.name,
    type: data.type,
    parentNodeId: data.parent_node_id,
  };
  return transformedData;
}

// Редактировать узел дерева
export async function editTreeNode(model: TreeNodeEditModel) {
  const {data} = await httpClient.put(`nodes/${model.id}`, {
    name: model.name,
    type: model.type,
    parent_node_id: model.parentNodeId,
  });

  // Возвращает отредактированный узел дерева
  const transformedData: TreeNodeEditModel = {
    id: model.id, // id не приходит с сервера если его не передавать
    name: data.name,
    type: data.type,
    parentNodeId: data.parent_node_id,
  };
  return transformedData;
}

// Удалить узел дерева
export async function removeTreeNode(model: TreeNodeRemoveModel) {
  const {data} = await httpClient.delete(`nodes/${model.id}`);

  //сервер возвращает edit модель удаленного узла дерева
  const transformedData: TreeNodeEditModel = {
    id: data.id,
    name: data.name,
    type: data.type,
    parentNodeId: data.parent_node_id,
  };
  return transformedData;
}

// Переместить узел дерева
export async function dragTreeNode(model: TreeNodeDragModel) {
  const {data} = await httpClient.put(`nodes/${model.from}/move`, {
    id_to: model.to,
    inside: model.inside,
  });
  return {
    to: data.id_to,
    inside: data.inside,
  };
}

// Фильтр узлов по типу (TODO: потом убрать)
function filterMachineNodes(node: SystemNode, type: SystemNodeType): SystemNode[] {
  const machineNodes: SystemNode[] = [];
  const stack = [node];
  while (stack.length) {
    const x = stack.pop();
    if (x?.type === type) machineNodes.push(x);
    if (x && x.children) x.children.forEach(c => stack.push(c));
  }
  return machineNodes;
}

// Получить список станков (TODO: Пока без фильтрации по Цеху и Предприятию)
export async function requestMachineNodes() {
  //TODO: пока на сервере нет отдельного метода получения узлов, потому получаю целиком дерево и фильтрую
  const {data} = await httpClient.get(`nodes/`);
  const trasformData = data as SystemNode[];
  let machineNodes: SystemNode[] = [];
  trasformData.forEach(
    x => (machineNodes = machineNodes.concat(filterMachineNodes(x, SystemNodeType.Machine))),
  );
  return machineNodes;
}

export async function requestNodeConfig(nodeId: string) {
  const {data} = await httpClient.get(`nodes/${nodeId}/configs/`);
  const transformData: NodeConfig[] = data.map((elem: any) => ({
    id: elem.config_id,
    name: elem.config_name,
    type: elem.config_type,
    coords: elem.coords,
  }));
  return transformData;
}

export async function requestNodeLinks(nodeId: string) {
  const {data} = await httpClient.get(`nodes/${nodeId}/nodes/`);
  const transformData: NodeLink[] = data.map((elem: any) => ({
    id: elem.id,
    name: elem.name,
    type: elem.type,
    coords: elem.coords,
  }));
  return transformData;
}

function mapPeriodsData(data: any): PeriodsData {
  const errorPeriods: ErrorPeriod[] = data.error_periods?.map((elem: any) => ({
    start: elem.start,
    end: elem.end,
    exists: elem.exists,
  }));

  const statusPeriods: StatusPeriod[] = data.status_periods?.map((elem: any) => ({
    start: elem.start,
    end: elem.end,
    status: elem.status,
    unstatus: elem.unstatus,
    color: elem.color,
    label: elem.label,
  }));

  return {
    startDate: data.start_date,
    endDate: data.end_date,
    errorPeriods: errorPeriods ?? [],
    statusPeriods: statusPeriods ?? [],
  };
}

function mapProductionData(data: any): ProductionData {
  return {
    errorsCount: data.errors_count,
    percent: data.percent,
    hours: data.hours,
    periodsData: mapPeriodsData(data.periods_data),
  };
}

function mapFacilityData(data: any): SystemNodeData {
  return {
    productionDay: mapProductionData(data.production_day),
    productionWeek: mapProductionData(data.production_week),
  };
}

function mapFacilityNodes(elem: any) {
  const res: SystemNode = {
    id: elem.id,
    name: elem.name,
    type: elem.type,
    data: elem.data ? mapFacilityData(elem.data) : undefined,
    coords: elem.coords,
  };

  const children: SystemNode[] = [];
  if (elem.children) {
    elem.children.forEach((c: any) => {
      children.push(mapFacilityNodes(c));
    });
  }
  res.children = [...children];
  return res;
}

/*
  Запрос на получение структуры предприятия для виджета Структура предприятия
  TODO: перенести в widgetApi
*/
export async function requestFacilityNodes(date: number) {
  const {data} = await httpClient.get(`dashboard/widgets/facility_structure/get_data/`, {
    params: {date},
  });
  // Рекурсивный маппинг
  return data.map((x: any) => mapFacilityNodes(x));
}

/*
Запрос на получение полной структуры предприятия для виджета Структура предприятия
*/
export async function requestFullThree() {
  try {
    const {data} = await httpClient.get(`nodes_rel/`);
    return data;
  } catch (e) {
    console.log('Error on Node tree request');
    console.log(e);
    return [];
  }
}
